import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Calendar, Download, FileText, Heart, Users, Compass, TrendingUp, CheckCircle2 } from 'lucide-react';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { SEO } from '../components/SEO';
import { getstrategiPlanContent } from '../data/strategiPlan';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

const PDF_URL =
  'https://customer-assets.emergentagent.com/job_a2119276-b11a-4d90-b267-fc420228fa77/artifacts/4trvzst0_Veitvet%20Sportsklubb%20%E2%80%93%20Strategisk%20Plan%202026%E2%80%932031%20%28edited%29.pdf';

const SectionHeading = ({ icon: Icon, label, title }) => (
  <div className="flex items-start gap-3 mb-6">
    <div className="bg-green-500/10 rounded-xl p-2.5 flex-shrink-0">
      <Icon className="w-5 h-5 text-green-600" />
    </div>
    <div>
      <p className="text-green-600 text-xs font-bold uppercase tracking-widest mb-1">{label}</p>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">{title}</h2>
    </div>
  </div>
);

const MetricsTable = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-slate-900 text-[#CCFF00]">
          {headers.map((h) => (
            <th key={h} className="text-left px-4 py-3 font-bold uppercase tracking-wider text-xs">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]} className="even:bg-slate-50 odd:bg-white">
            {row.map((cell, ci) => (
              <td key={`${row[0]}-${ci}`} className={`px-4 py-3 text-slate-700 ${ci === 0 ? 'font-semibold text-slate-900' : ''}`}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Goal = ({ goal }) => (
  <div
    data-testid={`strategi-goal-${goal.number}`}
    className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm"
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="bg-green-500 text-black font-bold w-10 h-10 rounded-xl flex items-center justify-center text-lg">
        {goal.number}
      </span>
      <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{goal.title}</h3>
    </div>
    <p className="text-slate-600 mb-6 leading-relaxed">{goal.intro}</p>

    <h4 className="text-sm font-bold uppercase tracking-wider text-green-600 mb-3">{t('pg.Strategiplan.ddb886')}</h4>
    <ul className="space-y-2 mb-6">
      {goal.targets.map((t) => (
        <li key={t} className="flex items-start gap-2 text-slate-700">
          <CheckCircle2 className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
          <span>{t}</span>
        </li>
      ))}
    </ul>

    <h4 className="text-sm font-bold uppercase tracking-wider text-green-600 mb-3">{t('pg.Strategiplan.d6621e')}</h4>
    <ul className="space-y-2 mb-6 list-disc list-inside text-slate-700 marker:text-green-500">
      {goal.subGoals.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>

    {goal.additionalSections?.map((sec) => (
      <div key={sec.title} className="mb-6 bg-slate-50 rounded-xl p-5 border border-slate-200">
        <h4 className="text-base font-bold text-slate-900 mb-2">{sec.title}</h4>
        {sec.text && <p className="text-slate-600 text-sm mb-3 leading-relaxed">{sec.text}</p>}
        <ul className="space-y-1 list-disc list-inside text-slate-700 text-sm marker:text-green-500">
          {sec.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </div>
    ))}

    <h4 className="text-sm font-bold uppercase tracking-wider text-green-600 mb-3">{t('pg.Strategiplan.4988d1')}</h4>
    <div className="mb-6">
      <MetricsTable headers={goal.metrics.headers} rows={goal.metrics.rows} />
    </div>

    <h4 className="text-sm font-bold uppercase tracking-wider text-green-600 mb-3">{t('pg.Strategiplan.0b4f37')}</h4>
    <ul className="space-y-2 list-disc list-inside text-slate-700 marker:text-green-500">
      {goal.actions.map((a) => (
        <li key={a}>{a}</li>
      ))}
    </ul>
  </div>
);

export const Strategiplan = () => {
  useTranslation();
  const data = getstrategiPlanContent();
  return (
    <div data-testid="strategiplan-page" className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <SEO
        title={t('pg.Strategiplan.30e95b')}
        description={t('pg.Strategiplan.3501cf')}
      />

      {/* Hero */}
      <section className="bg-white dark:bg-slate-800 py-16 px-4 sm:px-6 lg:px-8 border-b border-slate-200">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/strategi-sportsplan"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 transition-colors mb-8"
            data-testid="strategi-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('pg.Strategiplan.f04225')}
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center">
              <Target className="w-7 h-7 text-blue-500" />
            </div>
            <div>
              <p className="text-blue-600 font-medium uppercase tracking-widest mb-1 text-sm">{data.subtitle}</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
                {data.title}
              </h1>
            </div>
          </div>

          <p className="text-slate-600 text-lg leading-relaxed">{data.intro}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
              <Calendar className="w-4 h-4 text-green-600" />
              <span className="text-slate-700 text-sm font-medium">{t('pg.Strategiplan.770875')} {data.period}</span>
            </div>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="strategi-pdf-download"
              className="inline-flex items-center gap-2 bg-green-500 text-black font-bold rounded-lg px-4 py-2 hover:bg-green-500/90 transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              {t('pg.Strategiplan.98cdb4')}
            </a>
          </div>
        </div>
      </section>

      {/* Hvem er vi */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <SectionHeading icon={Users} label={t('pg.Strategiplan.3a0a65')} title={t('pg.Strategiplan.9825bb')} />
          <div className="space-y-4 text-slate-600 leading-relaxed">
            {data.whoWeAre.text.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>

          {/* Visjon */}
          <div className="mt-8 bg-gradient-to-br from-green-500/10 to-blue-500/5 border-l-4 border-green-500 rounded-r-xl p-6">
            <p className="text-green-600 text-xs font-bold uppercase tracking-widest mb-2">{t('pg.Strategiplan.9598df')}</p>
            <h3 className="text-3xl font-bold text-slate-900 mb-3">{data.whoWeAre.vision.title}</h3>
            <p className="text-slate-700 leading-relaxed">{data.whoWeAre.vision.text}</p>
          </div>

          {/* Hensikt */}
          <div className="mt-6 bg-slate-100 rounded-xl p-6">
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-2">{t('pg.Strategiplan.693756')}</p>
            <p className="text-slate-700 italic leading-relaxed">{data.whoWeAre.purpose}</p>
          </div>

          {/* Verdier */}
          <h3 className="mt-12 mb-4 text-xl font-bold text-slate-900 flex items-center gap-2">
            <Heart className="w-5 h-5 text-green-600" />
            {t('pg.Strategiplan.a8d669')}
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.whoWeAre.values.map((v) => (
              <div
                key={v.title}
                data-testid={`strategi-verdi-${v.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-green-500 transition-colors"
              >
                <h4 className="font-bold text-slate-900 mb-2">{v.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>

          {/* Kultur */}
          <h3 className="mt-12 mb-4 text-xl font-bold text-slate-900">{t('pg.Strategiplan.b820f1')}</h3>
          <div className="space-y-3 text-slate-600 leading-relaxed">
            {data.whoWeAre.culture.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Hvorfor strategi */}
      <section className="bg-white dark:bg-slate-800 py-16 px-4 sm:px-6 lg:px-8 border-y border-slate-200">
        <div className="max-w-4xl mx-auto">
          <SectionHeading icon={Compass} label={t('pg.Strategiplan.e5a785')} title={data.whyStrategy.title} />
          <div className="space-y-4 text-slate-600 leading-relaxed">
            {data.whyStrategy.text.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
          <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-slate-200">
            <p className="text-slate-900 font-bold mb-3">{t('pg.Strategiplan.086bf8')}</p>
            <ul className="space-y-2">
              {data.whyStrategy.audience.map((a) => (
                <li key={a.who} className="flex items-start gap-2 text-slate-700">
                  <span className="font-semibold">{a.who}</span>
                  <span className="text-slate-500">— {a.what}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Mål */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <SectionHeading icon={TrendingUp} label={t('pg.Strategiplan.2166d0')} title={t('pg.Strategiplan.597cfc')} />
          <div className="space-y-6">
            {data.goals.map((g) => (
              <Goal key={g.number} goal={g} />
            ))}
          </div>
        </div>
      </section>

      {/* Avslutning */}
      <section className="bg-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-3 text-sm">{t('pg.Strategiplan.e27172')}</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">{data.closing.title}</h2>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            {data.closing.text.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-slate-700">
            <p className="text-slate-400 text-sm mb-4">{data.closing.signedAt}</p>
            <ul className="space-y-1 text-slate-300">
              {data.closing.signatures.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <GrasrotandelenBanner />
    </div>
  );
};

export default Strategiplan;
